import React, { useState, useEffect } from 'react';
import { useLogto } from '@logto/react';
import { apiGet, apiDelete } from '../utils/api';
import { FolderOpen, X, FileText, BarChart3, Clock, Inbox, Loader2, Trash2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config/apiConfig';

interface SavedDataset {
  id: string;
  name: string;
  description: string;
  filename: string;
  created_at: string;
  updated_at: string;
  size_bytes: number;
}

interface SavedDatasetsBrowserProps {
  onSelectDataset: (datasetId: string, datasetName: string) => void;
  onClose: () => void;
}

export const SavedDatasetsBrowser: React.FC<SavedDatasetsBrowserProps> = ({
  onSelectDataset,
  onClose,
}) => {
  const [datasets, setDatasets] = useState<SavedDataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const logto = useLogto();

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<{ success: boolean; datasets: SavedDataset[] }>(
        API_ENDPOINTS.DATASET.LIST,
        logto
      );

      if (data.success) {
        setDatasets(data.datasets);
      } else {
        setError('Failed to load datasets');
      }
    } catch (err) {
      console.error('[SavedDatasetsBrowser] Error loading datasets:', err);
      setError(err instanceof Error ? err.message : 'Error loading datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (datasetId: string) => {
    if (!window.confirm('Are you sure you want to delete this dataset?')) {
      return;
    }

    setDeletingId(datasetId);
    try {
      await apiDelete(
        API_ENDPOINTS.DATASET.DELETE(datasetId),
        logto
      );

      setDatasets(datasets.filter((d) => d.id !== datasetId));
    } catch (err) {
      console.error('[SavedDatasetsBrowser] Error deleting dataset:', err);
      setError(err instanceof Error ? err.message : 'Error deleting dataset');
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="w-6 h-6" /> Saved Datasets
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-full w-8 h-8 flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading datasets...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {!loading && datasets.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Inbox className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-lg">No saved datasets yet</p>
              <p className="text-sm mt-2">Upload a dataset and save it to see it here</p>
            </div>
          )}

          {!loading && datasets.length > 0 && (
            <div className="space-y-3">
              {datasets.map((dataset) => (
                <div
                  key={dataset.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate text-lg">
                        {dataset.name}
                      </h3>
                      {dataset.description && (
                        <p className="text-sm text-gray-600 mt-1">{dataset.description}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {dataset.filename}</span>
                        <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {formatFileSize(dataset.size_bytes)}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(dataset.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => onSelectDataset(dataset.id, dataset.name)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => handleDelete(dataset.id)}
                        disabled={deletingId === dataset.id}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {deletingId === dataset.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
